import React from 'react';
import { PointsMultiplierEvent } from '../../data/go2LegendeMock';
import { Zap, Timer } from 'lucide-react';

interface MultiplierEventsProps {
  events: PointsMultiplierEvent[];
}

function calculateTimeRemaining(endDate: string): { days: number; hours: number } {
  const now = new Date();
  const end = new Date(endDate);
  const diff = end.getTime() - now.getTime();

  if (diff <= 0) {
    return { days: 0, hours: 0 };
  }

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

  return { days, hours };
}

function formatDateRange(startDate: string, endDate: string): string {
  const start = new Date(startDate);
  const end = new Date(endDate);

  const startDay = start.getDate().toString().padStart(2, '0');
  const startMonth = (start.getMonth() + 1).toString().padStart(2, '0');
  const endDay = end.getDate().toString().padStart(2, '0');
  const endMonth = (end.getMonth() + 1).toString().padStart(2, '0');

  return `${startDay}.${startMonth} - ${endDay}.${endMonth}.${end.getFullYear()}`;
}

const TYPE_BADGE_STYLES: Record<PointsMultiplierEvent['type'], string> = {
  flash: 'bg-red-100 text-red-700',
  seasonal: 'bg-green-100 text-green-700',
  partner: 'bg-blue-100 text-blue-700',
};

const TYPE_LABELS: Record<PointsMultiplierEvent['type'], string> = {
  flash: 'Flash',
  seasonal: 'Sezonsko',
  partner: 'Partner',
};

export function MultiplierEvents({ events }: MultiplierEventsProps) {
  const activeEvent = events.find((event) => event.active);
  const upcomingEvents = events.filter((event) => !event.active);

  return (
    <div className="w-full space-y-6">
      {/* Active Event Banner */}
      {activeEvent && (
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-brand-500 to-purple-600 p-8 text-white shadow-xl">
          <div className="relative z-10">
            {/* Event Emoji & Title */}
            <div className="flex items-center gap-3 mb-3">
              <span className="text-6xl">{activeEvent.emoji}</span>
              <div>
                <h2 className="text-3xl font-bold">{activeEvent.title}</h2>
                <p className="text-brand-50 mt-1">{activeEvent.description}</p>
              </div>
            </div>

            {/* Multiplier Display */}
            <div className="my-6">
              <div className="text-7xl font-black">
                {activeEvent.multiplier}x <span className="text-5xl">BODOVI</span>
              </div>
            </div>

            {/* Countdown Timer */}
            <div className="flex items-center gap-2 mb-6">
              <Timer className="w-5 h-5" />
              <span className="text-lg font-medium">
                Ostalo: {calculateTimeRemaining(activeEvent.endDate).days} dana,{' '}
                {calculateTimeRemaining(activeEvent.endDate).hours} sati
              </span>
            </div>

            {/* CTA Button */}
            <button className="bg-white text-brand-500 font-bold px-8 py-3 rounded-full hover:bg-brand-50 transition shadow-lg">
              Rezerviši sada!
            </button>
          </div>

          {/* Decorative Background */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full -mr-32 -mt-32"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white opacity-5 rounded-full -ml-24 -mb-24"></div>
        </div>
      )}

      {/* Upcoming Events */}
      {upcomingEvents.length > 0 && (
        <div>
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Zap className="w-5 h-5 text-go2-yellow" />
            Predstojeći Eventi
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {upcomingEvents.map((event) => (
              <div
                key={event.id}
                className="bg-white rounded-xl shadow-sm p-5 border border-gray-100 hover:shadow-md transition"
              >
                {/* Event Emoji & Title */}
                <div className="flex items-start gap-3 mb-3">
                  <span className="text-4xl">{event.emoji}</span>
                  <div className="flex-1">
                    <h4 className="font-bold text-gray-900 mb-1">{event.title}</h4>

                    {/* Type & Multiplier Badges */}
                    <div className="flex flex-wrap gap-2 mb-2">
                      <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${TYPE_BADGE_STYLES[event.type]}`}>
                        {TYPE_LABELS[event.type]}
                      </span>
                      <span className="bg-go2-yellow text-gray-900 text-xs font-bold px-2.5 py-1 rounded-full">
                        {event.multiplier}x
                      </span>
                    </div>
                  </div>
                </div>

                {/* Date Range */}
                <div className="text-sm text-gray-600 mb-2">
                  {formatDateRange(event.startDate, event.endDate)}
                </div>

                {/* Description */}
                <p className="text-sm text-gray-600">{event.description}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {events.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <Zap className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Trenutno nema aktivnih multiplikatora</p>
        </div>
      )}
    </div>
  );
}
